import { gql } from '@apollo/client';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Badge } from '@apollo/orbit';
import React, { useMemo } from 'react';

import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CheckWorkflowStatus } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';

import {
  GetTaskDisplayStatusTaskFragment,
  taskHasWarnings,
} from './getTaskDisplayStatus';

type BadgeVariant = 'info' | 'error' | 'success' | 'warn' | 'neutralOutlined';

interface Props {
  workflow: GraphQLTypes.CheckWorkflowStatusBadgeCheckWorkflowFragment;
  className?: string;
}

export const CheckWorkflowStatusBadgeCheckWorkflowFragment = gql`
  fragment CheckWorkflowStatusBadgeCheckWorkflowFragment on CheckWorkflow {
    id
    status
    tasks {
      id
      ...GetTaskDisplayStatusTaskFragment
    }
  }
  ${GetTaskDisplayStatusTaskFragment}
`;

/**
 * Note that this component is confusing to look at it and at some point we
 * should refactor it to be less convoluted. This component is used to render
 * the PASSED or FAILED badge for both the *entire* workflow and for each task
 * whether it is an operations task or build task
 */
export const CheckWorkflowStatusBadge = ({ workflow, className }: Props) => {
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );
  const badgeProps = useMemo((): {
    variant: BadgeVariant;
    children: CheckWorkflowStatus;
    icon?: React.ReactElement;
  } => {
    const hasWarnings = workflow.tasks.some((task) =>
      taskHasWarnings(task, shouldDescriptionsWarn),
    );

    switch (workflow.status) {
      case CheckWorkflowStatus.PASSED:
        return {
          variant: hasWarnings ? 'warn' : 'success',
          children: CheckWorkflowStatus.PASSED,
          icon: hasWarnings ? (
            <IconWarning className="icon-warning" />
          ) : undefined,
        };
      case CheckWorkflowStatus.FAILED:
        return {
          variant: 'error',
          children: CheckWorkflowStatus.FAILED,
        };
      case CheckWorkflowStatus.PENDING:
        return {
          variant: 'neutralOutlined',
          children: CheckWorkflowStatus.PENDING,
        };
      default:
        assertUnreachableOrReturnDefault(workflow.status, undefined);
        return {
          variant: 'neutralOutlined',
          children: CheckWorkflowStatus.PASSED,
        };
    }
  }, [workflow, shouldDescriptionsWarn]);

  return <Badge className={className} {...badgeProps} />;
};
